import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  Box,
  Chip,
  IconButton,
  Paper,
  Card,
  CardContent,
  Stack,
  Divider,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { formatDateField } from "../../../../../../base/FormatTime";

const AbbeyPainScaleDetailModal = ({ open, handleClose, assessment }) => {
  if (!assessment) return null;

  const getSignLabel = (value) => {
    const signChoices = {
      0: "Absent - 0",
      1: "Mild - 1",
      2: "Moderate - 2",
      3: "Severe - 3",
    };
    return signChoices[value] || "";
  };

  const getScoreColor = (score) => {
    if (score === 0) return "default";
    if (score === 1) return "warning";
    if (score >= 2) return "error";
    return "default";
  };

  const painScaleItems = [
    {
      label: "Whimpering/Groaning/Crying",
      score: assessment.signs_of_crying,
      details: assessment.sign_of_crying_details,
    },
    {
      label: "Tension/Fright/Frowning/Grimacing",
      score: assessment.signs_of_tension,
      details: assessment.sign_of_tension_details,
    },
    {
      label: "Fidgeting/Rocking/Guarding/Withdrawn",
      score: assessment.signs_of_fidgeting,
      details: assessment.sign_of_fidgeting_details,
    },
    {
      label: "Confusion/Refusal to Eat/Altered Patterns",
      score: assessment.signs_of_confusion,
      details: assessment.sign_of_confusion_details,
    },
    {
      label: "Temperature/Pulse/BP Changes/Perspiring",
      score: assessment.signs_of_temperature,
      details: assessment.sign_of_temperature_details,
    },
    {
      label: "Skin Tears/Pressure Areas/Arthritis",
      score: assessment.signs_of_skin_tears,
      details: assessment.sign_of_skin_tears_details,
    },
  ].filter((item) => item.score !== null);

  const InfoRow = ({ label, value, placeholder }) => {
    if (!value) return null;
    return (
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="body2"
          color="text.secondary"
          fontWeight="500"
          gutterBottom
        >
          {label}
        </Typography>
        <Typography variant="body2" color="text.primary">
          {value}
        </Typography>
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="h6" fontWeight="600">
              Abbey Pain Scale Assessment
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Score: {assessment.score}
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* Assessment Overview */}
        <Paper elevation={0} sx={{ p: 3, bgcolor: "grey.50" }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <InfoRow label="Service User" value={assessment.client_name} />
            </Grid>
            <Grid item xs={12} md={3}>
              <InfoRow
                label="Assessment Date"
                value={formatDateField(assessment.created_at)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <InfoRow label="Care Plan" value={assessment.care_plan_name} />
            </Grid>
            <Grid item xs={12} md={3}>
              <InfoRow label="Assessed By" value={assessment.created_by_name} />
            </Grid>
          </Grid>
        </Paper>

        {/* Pain Assessment Indicators */}
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight="600" gutterBottom sx={{ mb: 2 }}>
            Pain Assessment Indicators
          </Typography>

          <Grid container spacing={2}>
            {painScaleItems.map((item, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card variant="outlined" sx={{ height: "100%" }}>
                  <CardContent sx={{ p: 2 }}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      mb={1}
                    >
                      <Typography
                        variant="body2"
                        fontWeight="500"
                        sx={{ flex: 1, pr: 2 }}
                      >
                        {item.label}
                      </Typography>
                      <Chip
                        label={getSignLabel(item.score)}
                        color={getScoreColor(item.score)}
                        size="small"
                        variant="outlined"
                        sx={{ minWidth: "70px", fontWeight: "500" }}
                      />
                    </Box>
                    {item.details && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mt: 1,
                          p: 1.5,
                          bgcolor: "grey.50",
                          borderRadius: 1,
                          border: "1px solid",
                          borderColor: "grey.200",
                        }}
                      >
                        {item.details}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Divider />

        {/* Pain Information */}
        {(assessment.place_of_pain ||
          assessment.cause_of_pain ||
          assessment.pain_present_for ||
          assessment.pain_chronic ||
          assessment.last_pain_relief) && (
          <Box sx={{ p: 3 }}>
            <Typography
              variant="h6"
              fontWeight="600"
              gutterBottom
              sx={{ mb: 2 }}
            >
              Pain Information
            </Typography>

            <Card variant="outlined">
              <CardContent sx={{ p: 3 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Stack spacing={2}>
                      <InfoRow
                        label="Where is the pain?"
                        value={assessment.place_of_pain}
                        placeholder="e.g., Lower back and right shoulder"
                      />
                      <InfoRow
                        label="What is the cause of pain?"
                        value={assessment.cause_of_pain}
                        placeholder="e.g., Post-fall trauma"
                      />
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Stack spacing={2}>
                      <InfoRow
                        label="How long has the pain been present?"
                        value={assessment.pain_present_for}
                        placeholder="e.g., 3 days"
                      />
                      <InfoRow
                        label="Is the pain chronic/acute/acute on chronic?"
                        value={assessment.pain_chronic}
                      />
                      {assessment.last_pain_relief && (
                        <InfoRow
                          label="When was the last pain relief given?"
                          value={assessment.last_pain_relief}
                        />
                      )}
                    </Stack>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Box>
        )}

        {/* Pain Characteristics */}
        {(assessment.description_of_pain?.length > 0 ||
          assessment.make_pain_worse?.length > 0 ||
          assessment.make_pain_better?.length > 0) && (
          <>
            <Divider />
            <Box sx={{ p: 3 }}>
              <Typography
                variant="h6"
                fontWeight="600"
                gutterBottom
                sx={{ mb: 2 }}
              >
                Pain Characteristics
              </Typography>

              <Card variant="outlined">
                <CardContent sx={{ p: 3 }}>
                  <Grid container spacing={3}>
                    {assessment.description_of_pain?.length > 0 && (
                      <Grid item xs={12}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          fontWeight="500"
                          gutterBottom
                        >
                          Service user description of pain:
                        </Typography>
                        <Box display="flex" flexWrap="wrap" gap={1}>
                          {assessment.description_of_pain.map((desc, index) => (
                            <Chip
                              key={index}
                              label={desc}
                              variant="outlined"
                              size="small"
                            />
                          ))}
                        </Box>
                      </Grid>
                    )}

                    {assessment.make_pain_worse?.length > 0 && (
                      <Grid item xs={12} md={6}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          fontWeight="500"
                          gutterBottom
                        >
                          What makes the pain worse?
                        </Typography>
                        <Box display="flex" flexWrap="wrap" gap={1}>
                          {assessment.make_pain_worse.map((factor, index) => (
                            <Chip
                              key={index}
                              label={factor}
                              variant="outlined"
                              size="small"
                            />
                          ))}
                        </Box>
                      </Grid>
                    )}

                    {assessment.make_pain_better?.length > 0 && (
                      <Grid item xs={12} md={6}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          fontWeight="500"
                          gutterBottom
                        >
                          What makes the pain better?
                        </Typography>
                        <Box display="flex" flexWrap="wrap" gap={1}>
                          {assessment.make_pain_better.map((factor, index) => (
                            <Chip
                              key={index}
                              label={factor}
                              variant="outlined"
                              size="small"
                            />
                          ))}
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            </Box>
          </>
        )}

        {/* Comments */}
        {assessment.comments && (
          <>
            <Divider />
            <Box sx={{ p: 3 }}>
              <Typography
                variant="h6"
                fontWeight="600"
                gutterBottom
                sx={{ mb: 2 }}
              >
                Comments
              </Typography>
              <Card variant="outlined">
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    fontWeight="500"
                    gutterBottom
                  >
                    Comments including action required:
                  </Typography>
                  <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                    {assessment.comments}
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={handleClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AbbeyPainScaleDetailModal;
